import { conectarBotonesAgregar, escaparHtml, tarjetaProductoHtml } from '../componentes.js';
import { iconoLucide } from '../iconos.js';
import { navegarA } from '../router.js';

const PRODUCTOS_POR_PAGINA = 12;

function ordenarProductos(productos, orden) {
  const copia = [...productos];
  if (orden === 'precio-asc') return copia.sort((a, b) => Number(a.precio) - Number(b.precio));
  if (orden === 'precio-desc') return copia.sort((a, b) => Number(b.precio) - Number(a.precio));
  if (orden === 'nombre') return copia.sort((a, b) => a.nombre.localeCompare(b.nombre));
  return copia.sort((a, b) => Number(a.orden ?? 9999) - Number(b.orden ?? 9999));
}

function construirUrlCatalogo({
  categoriaSlug,
  busqueda,
  orden,
  vista,
  pagina,
  minPrecio,
  maxPrecio,
  disponible,
  categorias,
  destacado,
  nuevo,
  oferta,
  atributos,
} = {}) {
  const siguiente = new URLSearchParams();
  if (categoriaSlug) siguiente.set('categoria', categoriaSlug);
  if (busqueda) siguiente.set('q', busqueda);
  if (orden && orden !== 'destacados') siguiente.set('orden', orden);
  if (vista && vista !== 'grid') siguiente.set('vista', vista);
  if (pagina && pagina > 1) siguiente.set('pagina', String(pagina));
  if (minPrecio) siguiente.set('min_precio', String(minPrecio));
  if (maxPrecio) siguiente.set('max_precio', String(maxPrecio));
  if (disponible) siguiente.set('disponible', '1');
  if (categorias && categorias.length) siguiente.set('categorias', categorias.join(','));
  if (destacado) siguiente.set('destacado', '1');
  if (nuevo) siguiente.set('nuevo', '1');
  if (oferta) siguiente.set('oferta', '1');
  if (atributos) {
    for (const [clave, valores] of Object.entries(atributos)) {
      if (valores.length) siguiente.set(`attr_${clave}`, valores.join(','));
    }
  }
  return `/catalogo${siguiente.toString() ? `?${siguiente}` : ''}`;
}

function filtrarPorPrecio(productos, min, max) {
  return productos.filter((producto) => {
    const precio = Number(producto.precio ?? 0);
    if (min !== '' && min !== null && min !== undefined && precio < Number(min)) return false;
    if (max !== '' && max !== null && max !== undefined && precio > Number(max)) return false;
    return true;
  });
}

function filtrarPorDisponibilidad(productos, soloDisponibles) {
  if (!soloDisponibles) return productos;
  return productos.filter((producto) => {
    const variantes = (producto.variantes ?? []).filter((v) => v.estado !== 'oculta');
    if (!variantes.length) return false;
    return true;
  });
}

function filtrarPorMultiCategoria(productos, slugsCategorias) {
  if (!slugsCategorias.length) return productos;
  return productos.filter((producto) =>
    producto.categorias?.some((categoria) => slugsCategorias.includes(categoria.slug)),
  );
}

function filtrarPorDestacados(productos, filtroDestacado, filtroNuevo, filtroOferta) {
  if (!filtroDestacado && !filtroNuevo && !filtroOferta) return productos;
  return productos.filter((producto) => {
    if (filtroDestacado && !producto.es_destacado) return false;
    if (filtroNuevo && !producto.es_nuevo) return false;
    if (filtroOferta && !producto.es_oferta) return false;
    return true;
  });
}

function extraerAtributosDeProductos(productos) {
  const mapa = new Map();
  for (const producto of productos) {
    const variantes = (producto.variantes ?? []).filter((v) => v.estado !== 'oculta');
    for (const variante of variantes) {
      for (const valor of variante.valores ?? []) {
        const nombre = String(valor.atributo_nombre ?? valor.atributo?.nombre ?? 'Opcion');
        if (!mapa.has(nombre)) mapa.set(nombre, new Set());
        mapa.get(nombre).add(String(valor.valor));
      }
    }
  }
  const resultado = {};
  for (const [nombre, valores] of mapa) {
    resultado[nombre] = Array.from(valores).sort((a, b) => a.localeCompare(b));
  }
  return resultado;
}

function filtrarPorAtributos(productos, atributosFiltro) {
  if (!atributosFiltro || !Object.keys(atributosFiltro).length) return productos;
  return productos.filter((producto) => {
    const variantes = (producto.variantes ?? []).filter((v) => v.estado !== 'oculta');
    if (!variantes.length) return false;

    for (const [nombreAtributo, valoresSeleccionados] of Object.entries(atributosFiltro)) {
      if (!valoresSeleccionados.length) continue;
      const coincide = variantes.some((variante) =>
        (variante.valores ?? []).some((valor) => {
          const nombre = String(valor.atributo_nombre ?? valor.atributo?.nombre ?? 'Opcion');
          return nombre === nombreAtributo && valoresSeleccionados.includes(String(valor.valor));
        }),
      );
      if (!coincide) return false;
    }
    return true;
  });
}

function slugsDesdeParametro(valor) {
  return [
    ...new Set(
      String(valor ?? '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
}

function normalizarNivelFiltros(nivel) {
  const normalizado = String(nivel ?? 'basico').toLowerCase();
  if (normalizado === 'medio' || normalizado === 'pro') return normalizado;
  return 'basico';
}

function leerAtributosFiltro(params, permitido) {
  if (!permitido) return {};
  const atributosFiltro = {};
  for (const [clave, valor] of params.entries()) {
    if (clave.startsWith('attr_')) {
      const nombreAtributo = clave.slice(5);
      const valores = slugsDesdeParametro(valor);
      if (valores.length) atributosFiltro[nombreAtributo] = valores;
    }
  }
  return atributosFiltro;
}

function contarFiltrosActivos({
  categoriaSlug,
  busqueda,
  minPrecio,
  maxPrecio,
  disponible,
  categoriasSeleccionadas,
  destacado,
  nuevo,
  oferta,
  atributosFiltro,
}) {
  let count = 0;
  if (categoriaSlug) count++;
  if (busqueda) count++;
  if (minPrecio || maxPrecio) count++;
  if (disponible) count++;
  if (categoriasSeleccionadas.length) count++;
  if (destacado) count++;
  if (nuevo) count++;
  if (oferta) count++;
  count += Object.values(atributosFiltro).filter((valores) => valores.length).length;
  return count;
}

export function vistaCatalogo(contenedor, tienda, params, estado) {
  const nivelFiltros = normalizarNivelFiltros(tienda.empresa?.nivel_filtros);
  const filtrosMedio = nivelFiltros === 'medio' || nivelFiltros === 'pro';
  const filtrosPro = nivelFiltros === 'pro';
  const categoriaParam = params.get('categoria') ?? '';
  const categoriasParam = slugsDesdeParametro(params.get('categorias'));
  const categoriasSeleccionadas = filtrosMedio
    ? [...new Set([...(categoriaParam ? [categoriaParam] : []), ...categoriasParam])]
    : [];
  const categoriaSlug = filtrosMedio ? '' : categoriaParam;
  const busqueda = params.get('q') ?? '';
  const orden = params.get('orden') ?? 'destacados';
  const vista = params.get('vista') === 'lista' ? 'lista' : 'grid';
  const paginaActual = Math.max(1, Number(params.get('pagina') ?? 1) || 1);
  const minPrecio = params.get('min_precio') ?? '';
  const maxPrecio = params.get('max_precio') ?? '';
  const disponible = params.get('disponible') === '1';
  const categoriaTitulo =
    categoriaSlug || (categoriasSeleccionadas.length === 1 ? categoriasSeleccionadas[0] : '');
  const categoriaActual = tienda.categorias.find((categoria) => categoria.slug === categoriaTitulo);
  const destacado = filtrosMedio && params.get('destacado') === '1';
  const nuevo = filtrosMedio && params.get('nuevo') === '1';
  const oferta = filtrosMedio && params.get('oferta') === '1';

  const atributosDisponibles = filtrosPro ? extraerAtributosDeProductos(tienda.productos) : {};
  const atributosFiltro = leerAtributosFiltro(params, filtrosPro);
  const categoriasFiltro = filtrosMedio
    ? categoriasSeleccionadas
    : categoriaSlug
      ? [categoriaSlug]
      : [];

  let filtrados = tienda.productos.filter((producto) => {
    const texto = `${producto.nombre} ${producto.descripcion_corta ?? ''}`.toLowerCase();
    return !busqueda || texto.includes(busqueda.toLowerCase());
  });

  filtrados = filtrarPorMultiCategoria(filtrados, categoriasFiltro);
  filtrados = filtrarPorPrecio(filtrados, minPrecio, maxPrecio);
  filtrados = filtrarPorDisponibilidad(filtrados, disponible);
  filtrados = filtrarPorDestacados(filtrados, destacado, nuevo, oferta);
  filtrados = filtrarPorAtributos(filtrados, atributosFiltro);

  const productos = ordenarProductos(filtrados, orden);
  const totalPaginas = Math.max(1, Math.ceil(productos.length / PRODUCTOS_POR_PAGINA));
  const paginaSegura = Math.min(paginaActual, totalPaginas);
  const visibles = productos.slice(
    (paginaSegura - 1) * PRODUCTOS_POR_PAGINA,
    paginaSegura * PRODUCTOS_POR_PAGINA,
  );

  const filtrosActivos = contarFiltrosActivos({
    categoriaSlug,
    busqueda,
    minPrecio,
    maxPrecio,
    disponible,
    categoriasSeleccionadas,
    destacado,
    nuevo,
    oferta,
    atributosFiltro,
  });
  const estadoUrl = {
    categoriaSlug,
    busqueda,
    orden,
    vista,
    pagina: 1,
    minPrecio,
    maxPrecio,
    disponible,
    categorias: categoriasSeleccionadas,
    destacado,
    nuevo,
    oferta,
    atributos: atributosFiltro,
  };
  const urlCatalogo = (cambios = {}) => construirUrlCatalogo({ ...estadoUrl, ...cambios });
  const categoriasDrawer = filtrosMedio
    ? tienda.categorias
        .map(
          (categoria) => `
            <label class="sp-filtro-check sp-filtro-check--categoria">
              <input type="checkbox" value="${escaparHtml(categoria.slug)}" data-categoria ${categoriasSeleccionadas.includes(categoria.slug) ? 'checked' : ''} />
              <span>${escaparHtml(categoria.nombre)}</span>
            </label>`,
        )
        .join('')
    : `
        <label class="sp-filtro-check sp-filtro-check--categoria">
          <input type="radio" name="sp-categoria-unica" value="" ${!categoriaSlug ? 'checked' : ''} />
          <span>Todas</span>
        </label>
        ${tienda.categorias
          .map(
            (categoria) => `
              <label class="sp-filtro-check sp-filtro-check--categoria">
                <input type="radio" name="sp-categoria-unica" value="${escaparHtml(categoria.slug)}" ${categoria.slug === categoriaSlug ? 'checked' : ''} />
                <span>${escaparHtml(categoria.nombre)}</span>
              </label>`,
          )
          .join('')}
      `;

  contenedor.innerHTML = `
    <div class="sp-catalogo-v2">
      <section class="sp-page sp-contenedor">
        <div class="sp-catalogo-cabecera sp-catalogo-cabecera--nueva">
        <div>
          <p class="sp-eyebrow">Catálogo</p>
          <h1>${escaparHtml(categoriaActual?.nombre ?? 'Todos los productos')}</h1>
          <p>${productos.length} producto${productos.length === 1 ? '' : 's'} disponible${productos.length === 1 ? '' : 's'}.</p>
        </div>
        <div class="sp-catalogo-toolbar sp-catalogo-toolbar--nuevo" aria-label="Controles del catálogo">
          <div class="sp-buscador sp-buscador--cabecera">
            ${iconoLucide('Search')}
            <input id="sp-buscar" type="search" value="${escaparHtml(busqueda)}" placeholder="Buscar productos..." />
          </div>
          <div class="sp-catalogo-toolbar__grupo">
            <button class="sp-boton sp-boton--secundario sp-filtro-abrir ${filtrosActivos ? 'sp-filtro-abrir--activo' : ''}" type="button" data-sp-filtros-abrir aria-label="Abrir filtros">
              ${iconoLucide('SlidersHorizontal')}
              <span>Filtros</span>
              ${filtrosActivos ? `<span class="sp-filtro-badge">${filtrosActivos}</span>` : ''}
            </button>
            <label class="sp-select sp-select--cabecera">
              <select id="sp-orden">
                <option value="destacados" ${orden === 'destacados' ? 'selected' : ''}>Destacados</option>
                <option value="precio-asc" ${orden === 'precio-asc' ? 'selected' : ''}>Menor precio</option>
                <option value="precio-desc" ${orden === 'precio-desc' ? 'selected' : ''}>Mayor precio</option>
                <option value="nombre" ${orden === 'nombre' ? 'selected' : ''}>Nombre</option>
              </select>
            </label>
            <div class="sp-vista-toggle" role="group" aria-label="Vista">
              <a class="${vista === 'grid' ? 'sp-vista-toggle__activo' : ''}" href="${urlCatalogo({ vista: 'grid' })}" aria-label="Vista grid">${iconoLucide('Grid2X2')}</a>
              <a class="${vista === 'lista' ? 'sp-vista-toggle__activo' : ''}" href="${urlCatalogo({ vista: 'lista' })}" aria-label="Vista lista">${iconoLucide('List')}</a>
            </div>
          </div>
        </div>
      </div>

      <nav class="sp-catalogo-chips" aria-label="Categorías">
        <a class="sp-chip-categoria ${!categoriaSlug && categoriasSeleccionadas.length === 0 ? 'sp-chip-categoria--activo' : ''}" href="${urlCatalogo({ categoriaSlug: '', categorias: [] })}">Todos</a>
        ${tienda.categorias
          .map((categoria) => {
            const activo =
              categoria.slug === categoriaSlug || categoriasSeleccionadas.includes(categoria.slug);
            const href = filtrosMedio
              ? urlCatalogo({
                  categoriaSlug: '',
                  categorias: activo
                    ? categoriasSeleccionadas.filter((s) => s !== categoria.slug)
                    : [...categoriasSeleccionadas, categoria.slug],
                })
              : urlCatalogo({ categoriaSlug: activo ? '' : categoria.slug, categorias: [] });
            return `
              <a class="sp-chip-categoria ${activo ? 'sp-chip-categoria--activo' : ''}" href="${href}">
                ${escaparHtml(categoria.nombre)}
              </a>`;
          })
          .join('')}
      </nav>

      <div class="sp-catalogo-cuerpo">
        ${
          visibles.length
            ? `<div class="sp-grid-productos ${vista === 'lista' ? 'sp-grid-productos--lista' : ''}">${visibles
                .map((producto) => tarjetaProductoHtml(producto, tienda.empresa.moneda, estado))
                .join('')}</div>`
            : `<div class="sp-vacio">
                <span class="sp-vacio__icono" aria-hidden="true">${iconoLucide('Package')}</span>
                <h2>No encontramos productos</h2>
                <p>Prueba otra búsqueda o modifica los filtros.</p>
                <a class="sp-boton sp-boton--secundario" href="/catalogo">Limpiar filtros</a>
              </div>`
        }
        ${
          totalPaginas > 1
            ? `<nav class="sp-paginacion" aria-label="Paginación">
                <a class="${paginaSegura === 1 ? 'sp-paginacion__deshabilitado' : ''}" href="${urlCatalogo({ pagina: paginaSegura - 1 })}" aria-label="Página anterior">${iconoLucide('ArrowLeft')}</a>
                <span>Página ${paginaSegura} de ${totalPaginas}</span>
                <a class="${paginaSegura === totalPaginas ? 'sp-paginacion__deshabilitado' : ''}" href="${urlCatalogo({ pagina: paginaSegura + 1 })}" aria-label="Página siguiente">${iconoLucide('ArrowRight')}</a>
              </nav>`
            : ''
        }
      </div>
    </section>

    <div class="sp-filtros-overlay" hidden></div>
    <aside class="sp-filtros-drawer" data-sp-filtros-drawer aria-hidden="true" role="dialog" aria-modal="true" aria-label="Filtros avanzados">
      <div class="sp-filtros-drawer__cabecera">
        <div class="sp-filtros__titulo">${iconoLucide('SlidersHorizontal')}<strong>Filtros</strong></div>
        <button class="sp-icon-button sp-filtros__cerrar" type="button" data-sp-filtros-cerrar aria-label="Cerrar filtros">
          ${iconoLucide('X')}
        </button>
      </div>
      <div class="sp-filtros-drawer__cuerpo">
        <div class="sp-filtro-grupo">
          <label for="sp-buscar-filtro">Buscar</label>
          <div class="sp-buscador">
            ${iconoLucide('Search')}
            <input id="sp-buscar-filtro" type="search" value="${escaparHtml(busqueda)}" placeholder="Producto..." />
          </div>
        </div>

        <fieldset class="sp-filtro-grupo sp-filtro-grupo--categorias">
          <legend>Categorías</legend>
          <div class="sp-filtro-lista">
            ${categoriasDrawer}
          </div>
        </fieldset>

        <div class="sp-filtro-grupo">
          <strong>Precio</strong>
          <div class="sp-filtro-precio">
            <input id="sp-min-precio" type="number" placeholder="Mín" value="${escaparHtml(minPrecio)}" min="0" />
            <span>—</span>
            <input id="sp-max-precio" type="number" placeholder="Máx" value="${escaparHtml(maxPrecio)}" min="0" />
          </div>
        </div>

        <div class="sp-filtro-grupo">
          <label class="sp-filtro-check">
            <input type="checkbox" id="sp-disponible" ${disponible ? 'checked' : ''} />
            <span>Solo disponibles</span>
          </label>
        </div>

        ${
          filtrosMedio
            ? `
        <div class="sp-filtro-grupo">
          <strong>Destacados</strong>
          <div class="sp-filtro-atributo-opciones">
            <label class="sp-filtro-check">
              <input type="checkbox" id="sp-destacado" ${destacado ? 'checked' : ''} />
              <span>Destacados</span>
            </label>
            <label class="sp-filtro-check">
              <input type="checkbox" id="sp-nuevo" ${nuevo ? 'checked' : ''} />
              <span>Nuevos</span>
            </label>
            <label class="sp-filtro-check">
              <input type="checkbox" id="sp-oferta" ${oferta ? 'checked' : ''} />
              <span>En oferta</span>
            </label>
          </div>
        </div>
        `
            : ''
        }

        ${
          filtrosPro && Object.keys(atributosDisponibles).length > 0
            ? `
        <div class="sp-filtro-grupo">
          <strong>Atributos</strong>
          ${Object.entries(atributosDisponibles)
            .map(([nombre, valores]) => {
              const seleccionados = atributosFiltro[nombre] ?? [];
              return `
                <div class="sp-filtro-atributo">
                  <span class="sp-filtro-atributo-nombre">${escaparHtml(nombre)}</span>
                  <div class="sp-filtro-atributo-opciones">
                    ${valores
                      .map((valor) => {
                        const checked = seleccionados.includes(valor);
                        return `
                        <label class="sp-filtro-check">
                          <input type="checkbox" value="${escaparHtml(valor)}" data-atributo="${escaparHtml(nombre)}" ${checked ? 'checked' : ''} />
                          <span>${escaparHtml(valor)}</span>
                        </label>
                      `;
                      })
                      .join('')}
                  </div>
                </div>
              `;
            })
            .join('')}
        </div>
        `
            : ''
        }

        <div class="sp-filtros__acciones">
          <button class="sp-boton sp-boton--primario" type="button" id="sp-aplicar-filtros">Aplicar filtros</button>
          <button class="sp-boton sp-boton--secundario" type="button" id="sp-limpiar-filtros">Limpiar filtros</button>
        </div>
      </div>
    </aside>
    </div>
  `;

  const navegarCatalogo = (cambios = {}) => {
    navegarA(
      construirUrlCatalogo({
        categoriaSlug,
        busqueda,
        orden,
        vista,
        pagina: 1,
        minPrecio,
        maxPrecio,
        disponible,
        categorias: categoriasSeleccionadas,
        destacado,
        nuevo,
        oferta,
        atributos: atributosFiltro,
        ...cambios,
      }),
    );
  };

  const campoBuscar = contenedor.querySelector('#sp-buscar');
  const campoBuscarFiltro = contenedor.querySelector('#sp-buscar-filtro');
  const syncBuscar = (valor) => {
    const v = valor ?? '';
    if (campoBuscar) campoBuscar.value = v;
    if (campoBuscarFiltro) campoBuscarFiltro.value = v;
  };
  const navegarBusqueda = (valor) => {
    syncBuscar(valor);
    navegarCatalogo({ busqueda: valor });
  };

  campoBuscar?.addEventListener('keydown', (evento) => {
    if (evento.key !== 'Enter') return;
    navegarBusqueda(campoBuscar.value.trim());
  });

  campoBuscarFiltro?.addEventListener('keydown', (evento) => {
    if (evento.key !== 'Enter') return;
    navegarBusqueda(campoBuscarFiltro.value.trim());
  });

  contenedor.querySelector('#sp-orden')?.addEventListener('change', (evento) => {
    navegarCatalogo({ orden: evento.target.value });
  });

  const inputMin = contenedor.querySelector('#sp-min-precio');
  const inputMax = contenedor.querySelector('#sp-max-precio');
  const checkDisponible = contenedor.querySelector('#sp-disponible');

  contenedor.querySelector('#sp-aplicar-filtros')?.addEventListener('click', () => {
    const nuevosAtributos = {};
    if (filtrosPro)
      contenedor.querySelectorAll('[data-atributo]:checked').forEach((cb) => {
        const nombre = cb.dataset.atributo;
        if (!nuevosAtributos[nombre]) nuevosAtributos[nombre] = [];
        nuevosAtributos[nombre].push(cb.value);
      });
    const categoriasDrawerSeleccionadas = filtrosMedio
      ? Array.from(contenedor.querySelectorAll('[data-categoria]:checked')).map((cb) => cb.value)
      : [];
    const categoriaBasica =
      contenedor.querySelector('input[name="sp-categoria-unica"]:checked')?.value ?? '';
    const buscarValor = campoBuscarFiltro?.value.trim() ?? busqueda;
    syncBuscar(buscarValor);
    navegarCatalogo({
      categoriaSlug: filtrosMedio ? '' : categoriaBasica,
      busqueda: buscarValor,
      minPrecio: inputMin?.value ?? '',
      maxPrecio: inputMax?.value ?? '',
      disponible: checkDisponible?.checked ? '1' : '',
      destacado: filtrosMedio && contenedor.querySelector('#sp-destacado')?.checked ? '1' : '',
      nuevo: filtrosMedio && contenedor.querySelector('#sp-nuevo')?.checked ? '1' : '',
      oferta: filtrosMedio && contenedor.querySelector('#sp-oferta')?.checked ? '1' : '',
      categorias: categoriasDrawerSeleccionadas,
      atributos: nuevosAtributos,
    });
  });

  contenedor.querySelector('#sp-limpiar-filtros')?.addEventListener('click', (evento) => {
    evento.preventDefault();
    navegarA('/catalogo');
  });

  const filtroMovilAbrir = contenedor.querySelector('[data-sp-filtros-abrir]');
  const drawer = contenedor.querySelector('[data-sp-filtros-drawer]');
  const overlay = contenedor.querySelector('.sp-filtros-overlay');
  let ultimoEnfoqueFiltros = null;
  const cerrarFiltros = () => {
    drawer?.classList.remove('sp-filtros-drawer--abierto');
    drawer?.setAttribute('aria-hidden', 'true');
    overlay?.classList.remove('sp-filtros-overlay--visible');
    overlay?.setAttribute('hidden', '');
    document.body.classList.remove('sp-bloqueado');
    if (ultimoEnfoqueFiltros?.isConnected) ultimoEnfoqueFiltros.focus();
    ultimoEnfoqueFiltros = null;
  };
  const abrirFiltros = () => {
    ultimoEnfoqueFiltros = document.activeElement;
    drawer?.classList.add('sp-filtros-drawer--abierto');
    drawer?.setAttribute('aria-hidden', 'false');
    overlay?.removeAttribute('hidden');
    overlay?.classList.add('sp-filtros-overlay--visible');
    document.body.classList.add('sp-bloqueado');
    drawer?.querySelector('input, button, select, a[href]')?.focus();
  };
  filtroMovilAbrir?.addEventListener('click', abrirFiltros);
  contenedor.querySelector('[data-sp-filtros-cerrar]')?.addEventListener('click', cerrarFiltros);
  overlay?.addEventListener('click', cerrarFiltros);
  drawer?.addEventListener('keydown', (evento) => {
    if (evento.key === 'Escape') cerrarFiltros();
  });

  conectarBotonesAgregar(contenedor);
}
